import type { Treatment } from '../../types/care';
import { LEAFLETS, standardBring } from './shared';

export const infusionsAndDevices: Treatment[] = [
  {
    slug: 'ketamine-infuus',
    name: 'Ketamine-infuus',
    category: 'Infuus & medicatie',
    summary: 'Infuus met ketamine bij centrale en neuropathische pijnsyndromen.',
    intro: [
      'Ketamine is een anestheticum dat onder andere bij narcose wordt gebruikt. Via een infuus komt het in het centrale zenuwstelsel terecht, waar het receptoren bij het ruggenmerg blokkeert. Zo wordt pijn minder goed doorgegeven aan de hersenen.',
      'Het infuus wordt ingezet bij centrale pijnsyndromen (bijvoorbeeld na een CVA, bij MS of de ziekte van Parkinson) en neuropathische pijn, zoals pijn na gordelroos, CRPS (dystrofie), pijn bij diabetes of na chemotherapie, amputatie, bestraling of verwonding.',
    ],
    procedure: [
      'U krijgt een infuus en wordt aangesloten op de monitor; hartslag, bloeddruk en zuurstofgehalte worden geregistreerd.',
      'De dosering wordt aangepast op geleide van uw klachten. De behandeling kan in totaal 6 uur duren. Als de ketamine is toegediend en de controles stabiel zijn, mag u onder begeleiding naar huis.',
    ],
    facts: { guidance: 'Infuus met monitoring', duration: 'Tot 6 uur', noDriving: true },
    preparation: [
      'U mag thuis een normaal ontbijt eten.',
      'Neem iets mee om te doen (bijvoorbeeld muziek met oordopjes), lunch, makkelijke kleding, uw telefoon en de medicatie die u overdag gebruikt.',
      'Zorg voor vervoer naar huis — u mag niet zelf rijden in verband met mogelijke duizeligheid.',
    ],
    report: [
      'Bloedverdunners en ander medicijngebruik (neem een overzicht mee)',
      'Zwangerschap',
      'Psychiatrische voorgeschiedenis',
      'Hartklachten, doorgemaakt hartinfarct of hartfalen',
      'Verhoogde bloeddruk',
      'Glaucoom',
      'Schildklieraandoening',
    ],
    bring: standardBring,
    result: ['Het effect wordt samen met de pijnspecialist geëvalueerd; daarna worden eventuele vervolgstappen besproken.'],
    complications: [
      'Tijdens de behandeling worden bloeddruk, hartritme en zuurstofgehalte regelmatig gecontroleerd. Mogelijke complicaties zijn bewustzijnsverlaging, bloeddrukverhoging, ademhalingsdepressie en hallucinaties.',
    ],
    sideEffects: [
      {
        title: 'Mogelijke bijwerkingen tijdens de toediening',
        items: [
          'Duizeligheid, een dronken gevoel of sufheid',
          'Verwardheid',
          'Misselijkheid of speekselvloed',
          'Onbegrepen pijn in de ledematen of motorische onrust',
        ],
      },
    ],
    pdf: `${LEAFLETS}/Ketamine-infuus-VER-FOL-013-OPE-V3.0.pdf`,
  },
  {
    slug: 'lidocaine-intraveneus',
    name: 'Lidocaïne-infuus intraveneus',
    category: 'Infuus & medicatie',
    summary: 'Lidocaïne via een infuus in de ader bij centrale en neuropathische pijn.',
    intro: [
      'Lidocaïne is een lokaal verdovingsmiddel. Via een infuus komt het in het centrale zenuwstelsel terecht, waar het een blokkerend effect heeft op de zenuwcellen.',
      'Het infuus wordt ingezet bij centrale pijnsyndromen en neuropathische pijn, zoals pijn na gordelroos, CRPS, pijn bij diabetes, na een amputatie, bestraling of verwonding.',
    ],
    procedure: [
      'U wordt aangesloten op de monitor (hartslag, bloeddruk, zuurstof) en krijgt een infuus.',
      'Via een infuuspomp krijgt u gedurende 4 uur lidocaïne toegediend. Als de controles stabiel zijn, mag u onder begeleiding naar huis.',
    ],
    facts: { guidance: 'Infuus met monitoring', duration: '4 uur', sessions: 'Vaak 3 – 4 behandelingen', noDriving: true },
    report: [
      'Bloedverdunners en ander medicijngebruik (neem een overzicht mee)',
      'Allergieën',
      'Zwangerschap',
      'Hartklachten (ritmestoornis, doorgemaakt hartinfarct, hartfalen)',
    ],
    bring: standardBring,
    result: [
      'Het resultaat kan na enkele dagen merkbaar zijn, maar soms duurt het enkele weken of treedt het effect pas na 3 of 4 behandelingen op.',
      'Na de vierde behandeling belt de verpleegkundig pijnconsulent u om de behandeling te evalueren.',
    ],
    sideEffects: [
      {
        title: 'Mogelijke bijwerkingen',
        items: ['Jeuk', 'Versnelde hartslag', 'Verhoogde bloeddruk', 'Bij hinderlijke bijwerkingen wordt de dosering aangepast'],
      },
    ],
    pdf: `${LEAFLETS}/Lidocaine-infuus-intraveneus-VER-FOL-014-OPE-V2.0.pdf`,
  },
  {
    slug: 'lidocaine-subcutaan',
    name: 'Lidocaïne subcutaan infuus',
    category: 'Infuus & medicatie',
    summary: 'Lidocaïne onder de huid om een overactief immuunsysteem te remmen, onder meer bij post-COVID.',
    intro: [
      'Lidocaïne wordt meestal gebruikt om de huid te verdoven. Bij deze behandeling gebruiken we het om het immuunsysteem tot rust te brengen.',
      'Het medicijn wordt onder de huid toegediend en direct opgenomen in het lymfestelsel — de "snelweg" van het immuunsysteem. Zo werkt het effectiever dan via het bloed.',
      'Indicaties zijn chronische pijn en systeemziektes met laaggradige ontstekingen en zenuwpijn, zoals reuma, fibromyalgie, postvirale syndromen, de ziekte van Lyme, prikkelbare darm syndroom en aanhoudende post-COVID klachten.',
    ],
    procedure: [
      'Het infuus wordt in 2,5 uur toegediend. U zit in een relaxstoel en kunt lezen, muziek luisteren of een film kijken. Toiletbezoek is toegestaan; verder blijft u zitten.',
      'Tussendoor worden bloeddruk, hartritme en zuurstofgehalte gecontroleerd. Uw eigen medicijnen kunt u gewoon blijven gebruiken.',
      'Meestal wordt tegelijk gestart met lidocaïne onder de tong (sublinguaal) ter ondersteuning; de instructies krijgt u in de kliniek.',
    ],
    facts: { guidance: 'Onderhuids infuus', duration: '2,5 uur', sessions: 'Elke 6 – 8 weken', noDriving: true },
    preparation: ['Draag comfortabele kleding. Koffie en thee zijn aanwezig; zorg zelf voor uw lunch.', 'Neem een boek, laptop of iets anders mee om te doen.'],
    report: ['Zwangerschap', 'Allergieën', 'Medicijngebruik'],
    bring: standardBring,
    result: [
      'Om het resultaat goed te beoordelen zijn drie infusies nodig met tussenpozen van 6 tot 8 weken. Na het vierde infuus wordt de behandeling geëvalueerd en de follow-up bepaald.',
    ],
    sideEffects: [
      {
        title: 'Mogelijke bijwerkingen',
        items: [
          'Lokale roodheid en/of zwelling bij de insteekplaats',
          'Blauwe plek bij de insteekopening',
          'Door de zeer lage concentratie in het bloed is de kans op andere bijwerkingen verwaarloosbaar',
        ],
      },
    ],
    pdf: `${LEAFLETS}/Lidocaine-subcutaan-infuus-in-de-kliniek-VER-FOL-015-OPE-V3.0.pdf`,
  },
  {
    slug: 'qutenza',
    name: 'Qutenza® behandeling',
    category: 'Infuus & medicatie',
    summary: 'De "peperpleister" met 8% capsaïcine bij plaatselijke zenuwpijn.',
    intro: [
      'Qutenza is een huidpleister met een hoge dosis capsaïcine (8%), de stof uit paprika en Spaanse peper — daarom ook wel de "peperpleister".',
      'Capsaïcine activeert pijnprikkelontvangers in de huid, die zich daardoor terugtrekken. Als de behandeling aanslaat, kan tot 12 weken pijnverlichting optreden.',
    ],
    procedure: [
      'De verpleegkundig pijnconsulent tekent het pijngebied af op de huid. De pleister wordt op maat geknipt, aangebracht en met een rekverband stevig aangedrukt.',
      'Na 60 minuten (voor de voeten 30 minuten) wordt de pleister verwijderd en de huid gereinigd.',
    ],
    facts: { guidance: 'Pleister op de huid', duration: '30 – 60 minuten', sessions: 'Herhaalbaar na 3 – 4 maanden', noDriving: true },
    preparation: [
      'De behandeling kan pijnlijk zijn: neem een uur van tevoren 1 gram paracetamol in.',
      'Neem een coolpack met theedoek mee om de huid na afloop te koelen.',
      'Scheer, scrub of smeer de huid vooraf niet in. Trek gemakkelijke kleding aan.',
      'Neem iemand mee die u naar huis kan brengen.',
    ],
    report: ['Alle medicijnen die u gebruikt', 'Allergieën', 'Zwangerschap'],
    bring: standardBring,
    result: [
      'Pijnvermindering treedt gemiddeld na 7 – 14 dagen op en kan weken aanhouden. Als de pijnprikkelontvangers terugkeren, kan de zenuwpijn terugkomen. De behandeling kan na 3 tot 4 maanden worden herhaald.',
      'De eerste dagen kan de huid warm aanvoelen en pijnlijk zijn. Koel met een coolpack in een theedoek, douche lauw, vermijd de zon en sport niet intensief.',
    ],
    sideEffects: [
      {
        title: 'Mogelijke bijwerkingen',
        items: [
          'Hoesten of niezen bij het inademen van capsaïcine',
          'Roodheid, branderigheid en pijn van de huid, een paar dagen',
          'Capsaïcine kan nog enkele dagen uit de huid "lekken" — was bij contact goed uw handen',
        ],
      },
    ],
    pdf: `${LEAFLETS}/Qutenza-VER-FOL-020-OPE-V3.0.pdf`,
  },
  {
    slug: 'medicatie-neuropathische-pijn',
    name: 'Medicatie tegen neuropathische pijn',
    category: 'Infuus & medicatie',
    summary: 'Medicatie tegen zenuwpijn, voorgeschreven door de pijnspecialist.',
    intro: ['Medicatie tegen neuropathische pijn (zenuwpijn) wordt voorgeschreven door uw pijnspecialist.'],
    procedure: [
      'Heeft uw pijnspecialist medicatie voorgeschreven en heeft u een herhaalrecept nodig? Dat kunt u aanvragen via de pagina Herhaalmedicatie. Bestel uw medicatie tijdig en reken op 5 werkdagen verwerkingstijd.',
    ],
    facts: {},
    result: [],
  },
  {
    slug: 'iontoforese',
    name: 'Iontoforese',
    category: 'Niet-invasief',
    summary: 'Pijnstillende medicatie via de huid met een speciale pleister en zwakstroom — zonder naald.',
    intro: [
      'Bij iontoforese krijgt u pijnstillende medicatie via de huid toegediend, met een speciale pleister en een zwak elektrisch veld. Afhankelijk van uw klachten is dat Ketanest-S® (vooral bij zenuwpijn) of dexamethason (vooral bij gewrichts- of lokale spierpijn).',
      'Voordelen: er is geen injectie nodig, de kans op bijwerkingen is klein omdat de medicatie nauwelijks in de bloedbaan komt, en de medicatie geeft direct een gedeeltelijke blokkade in het zenuwstelsel.',
    ],
    procedure: [
      'De pijnverpleegkundige brengt het medicijn aan op een absorberende pleister (elektrode 1) op de plek waar u de meeste pijn voelt. Vlakbij komt een tweede elektrode. Beide worden op een apparaatje aangesloten.',
      'Na ongeveer 20 minuten is de behandeling klaar. De huid onder de elektroden kan enkele uren tot dagen rood en gevoelig zijn.',
    ],
    facts: { guidance: 'Pleister met zwakstroom', duration: '± 20 minuten', sessions: '3 – 5 keer, elke 1 – 2 weken' },
    report: ['Zwangerschap', 'Allergieën', 'Pacemaker of ICD'],
    bring: standardBring,
    result: [
      'De pijn neemt in de dagen en weken na de behandelingen af; vooral dexamethason heeft tijd nodig. Soms treedt pas na enkele behandelingen pijnvermindering op.',
      'Na ongeveer 5 weken belt de verpleegkundig pijnconsulent u voor een evaluatie.',
    ],
    sideEffects: [
      { title: 'Ketanest-S®', items: ['Duizeligheid, misselijkheid en hoofdpijn — zelden, licht van aard en snel voorbij'] },
      { title: 'Dexamethason', items: ['Hoofdpijn, die 1 tot 2 dagen kan aanhouden'] },
    ],
    pdf: `${LEAFLETS}/Iontoforese-VER-FOL-012-OPE-V3.0.pdf`,
  },
  {
    slug: 'tens',
    name: 'TENS',
    category: 'Niet-invasief',
    summary: 'Transcutane Elektrische Neuro Stimulatie: pijnprikkels blokkeren via elektroden op de huid.',
    intro: [
      'Via elektroden op de huid worden de onderliggende zenuwvezels gestimuleerd, waardoor pijnprikkels kunnen worden geblokkeerd. TENS is eenvoudig, niet pijnlijk en zelf uit te voeren — bij acute én chronische pijn.',
      'TENS verstoort de zenuwgeleiding, stimuleert de afgifte van lichaamseigen pijnstillers (endorfines) en werkt spierontspannend. Het neemt de oorzaak van de pijn niet weg, maar kan de pijnprikkel wel blokkeren.',
      'Indicaties zijn zenuwpijn, pijn door letsel, gewrichts- of spierpijn en pijn door doorbloedingsstoornissen.',
    ],
    procedure: [
      'De pijnconsulent legt de werking van het apparaat uit en bepaalt samen met u waar de elektroden komen.',
      'U kunt TENS overdag zelf toepassen en de duur zelf bepalen — zittend, liggend of terwijl u bezig bent. Gebruik het niet ’s nachts of tijdens het autorijden. De huid onder de elektroden moet gaaf en vetvrij zijn.',
    ],
    facts: { guidance: 'Elektroden op de huid', sessions: 'Proefperiode van 4 weken' },
    report: ['Zwangerschap', 'Epilepsie', 'Pacemaker of ICD', 'Beschadigde huid op de plaats van behandeling'],
    bring: standardBring,
    result: [
      'Na een proefperiode van vier weken evalueert u samen met de pijnconsulent het effect, eventueel telefonisch. Heeft u er baat bij, dan kunt u het apparaat behouden; zo niet, dan krijgt u instructies om het te retourneren.',
      'TENS heeft geen bijwerkingen; soms kan de huid gevoelig zijn voor de elektroden. Langdurig gebruik is niet schadelijk.',
    ],
    pdf: `${LEAFLETS}/TENS-VER-FOL-022-OPE-V3.0.pdf`,
  },
];
